import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { useAppContext } from '../contexts/AppContext'
import { useFilterState } from '../hooks/useFilterState'
import { useViewMode } from '../hooks/useViewMode'
import { usePullToRefresh } from '../hooks/usePullToRefresh'
import { filterVenues, getNeighborhoods, isVenueActiveNow, isScheduleActiveNow, distanceMiles, fmtDistance, getBusinessOpenMins } from '../utils/filters'
import { sortVenuesByMode } from '../utils/scoring'
import { buildBestPicksSections } from '../utils/bestPicks'
import { Analytics } from '../services/analytics'
import { FilterPanel } from '../components/FilterPanel'
import { SortBar } from '../components/SortBar'
import { MapView } from '../components/MapView'
import { ViewToggle } from '../components/ViewToggle'
import { VenueCard } from '../components/VenueCard'
import { BestPicksRow } from '../components/BestPicksRow'
import { SplashScreen } from '../components/SplashScreen'
import { EmailCapture, useEmailCapture } from '../components/EmailCapture'
import { SponsoredBanner } from '../components/SponsoredBanner'
import type { BrandAd } from '../components/SponsoredBanner'
import { getActiveBrandAds, getBrandAdById } from '../services/brandAdService'
import { getCurrentWeeklyFeature, type WeeklyFeature } from '../services/weeklyFeatureService'
import { WeeklyFeatureCard } from '../components/WeeklyFeatureCard'
import { supabase } from '../lib/supabase'
import { getUpcomingEvents } from '../services/venueEventService'
import type { VenueEvent } from '../services/venueEventService'
import { EventsSection } from '../components/EventCard'
import type { Venue } from '../types'
import { fmtDealValue } from '../utils/dealFormat'

// ─── PAGINATION CONSTANTS ────────────────────────────────────────────────────
const INITIAL_PAGE_SIZE = 30
const LOAD_MORE_SIZE = 20

// ─── ROTATING AD ────────────────────────────────────────────────────────────
interface RotatingAdProps {
  ads: BrandAd[]
  slotIndex: number
}

const RotatingAd = React.memo(function RotatingAd({ ads, slotIndex }: RotatingAdProps) {
  const [adIndex, setAdIndex] = useState(() => slotIndex % ads.length)

  useEffect(() => {
    if (ads.length <= 1) return
    const id = setInterval(() => setAdIndex(i => (i + 1) % ads.length), 15_000)
    return () => clearInterval(id)
  }, [ads.length])

  const ad = ads[adIndex]
  if (!ad) return null
  return <SponsoredBanner ad={ad} />
})

// ─── TIME-AWARE HERO ────────────────────────────────────────────────────────
// Orange when bars are live, dark charcoal otherwise.
// Shows cheapest drink + best food deal callouts from live venue data.

const BrowseHero = React.memo(function BrowseHero({ venues, city }: { venues: Venue[]; city: string }) {
  const now = new Date()
  const currentMins = now.getHours() * 60 + now.getMinutes()
  const todayAbbr = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][now.getDay()]

  const liveVenues = useMemo(() => venues.filter(v => isVenueActiveNow(v)), [venues])
  const liveCount = liveVenues.length
  const totalDeals = useMemo(() => venues.reduce((acc, v) => acc + (v.schedules?.length ?? 0), 0), [venues])

  // ── EFFECTIVE UPCOMING STARTS ──────────────────────────────────────────
  // A happy hour EFFECTIVELY starts at the later of (deal start, doors
  // open). The old logic used raw schedule start_time only, which produced
  // "Next happy hour starts in 4h 38m" at 9:22 AM while a bar with an
  // all-day deal opened at 11 — the true next start. All-day and
  // already-started-on-paper schedules are included, clamped to opening
  // time; venues closed today are excluded entirely.
  const upcomingStartsMins = useMemo(() => {
    const day = now.getDay()
    const out: number[] = []
    venues.forEach(v => {
      const openMins = getBusinessOpenMins(v, day)
      if (openMins === null) return  // closed today — deals can't run
      ;(v.schedules ?? []).forEach(s => {
        if (!s.days.includes(todayAbbr as any)) return
        let schedStart = 0
        let schedEnd = 24 * 60
        if (!s.is_all_day) {
          if (!s.start_time || !s.end_time) return
          const [sh, sm] = s.start_time.split(':').map(Number)
          const [eh, em] = s.end_time.split(':').map(Number)
          schedStart = sh * 60 + (sm || 0)
          schedEnd = eh * 60 + (em || 0)
          if (schedEnd <= schedStart) schedEnd += 24 * 60  // spans midnight
        }
        const effStart = Math.max(schedStart, openMins)
        if (effStart >= schedEnd) return  // deal over before doors open
        const minsUntil = effStart - currentMins
        if (minsUntil > 0) out.push(minsUntil)
      })
    })
    return out
  }, [venues, todayAbbr, currentMins])

  const startingSoonCount = useMemo(
    () => upcomingStartsMins.filter(m => m <= 90).length,
    [upcomingStartsMins]
  )

  const nextStart = useMemo(() => {
    if (upcomingStartsMins.length === 0) return null
    const earliest = Math.min(...upcomingStartsMins)
    const h = Math.floor(earliest / 60)
    const m = earliest % 60
    if (h === 0) return `${m}m`
    return m === 0 ? `${h}h` : `${h}h ${m}m`
  }, [upcomingStartsMins])

  // Cheapest drink from live venues — only from CURRENTLY ACTIVE schedules
  // A venue may be "live" but have multiple schedules; only scan the ones
  // actually running right now to avoid showing Monday-only deals on Tuesday.
  //
  // A discount deal ("$1 off Wells") has no comparable dollar price — it's
  // not a $1 well, it's $1 cheaper than usual. Pass 1 only compares deals
  // with a real `price` (same principle as VenueCard's hasComparablePrice),
  // so a discount can't out-rank a genuine flat price by accident. If no
  // priced deal exists, Pass 2 falls back to ANY drink deal — including
  // discounts — and must format it correctly instead of showing bare text.
  const cheapestDrink = useMemo(() => {
    let best: { price: number | null; value: number | null; valueType: string; desc: string; venue: string; venueId: string } | null = null

    // Pass 1: structured deals with an explicit flat price, active schedule only
    for (const v of liveVenues) {
      const activeSchedules = (v.schedules ?? []).filter(s => isScheduleActiveNow(s))
      for (const s of activeSchedules) {
        for (const d of s.deals) {
          const vt = (d as any).value_type
          const isDiscount = vt === 'amount_off' || vt === 'percent_off'
          if (!isDiscount && d.price != null && ['beer','cocktail','liquor','wine'].includes(d.type)) {
            if (!best || best.price == null || d.price < best.price) {
              best = { price: d.price, value: null, valueType: 'price', desc: d.description, venue: v.name, venueId: v.id }
            }
          }
        }
      }
    }
    if (best) return best

    // Pass 2: any drink deal without a comparable price — discount or
    // plain description — active schedule only. First match wins (no
    // price to rank by).
    for (const v of liveVenues) {
      const activeSchedules = (v.schedules ?? []).filter(s => isScheduleActiveNow(s))
      for (const s of activeSchedules) {
        for (const d of s.deals) {
          if (['beer','cocktail','liquor','wine'].includes(d.type) && d.description.trim()) {
            return {
              price: null,
              value: (d as any).value ?? null,
              valueType: (d as any).value_type ?? 'price',
              desc: d.description, venue: v.name, venueId: v.id,
            }
          }
        }
        // Pass 3: fall back to deal_text if no structured deals
        if ((!s.deals || s.deals.length === 0) && s.deal_text?.trim()) {
          return { price: null, value: null, valueType: 'price', desc: s.deal_text, venue: v.name, venueId: v.id }
        }
      }
    }
    return null
  }, [liveVenues])

  // Best food deal — only from currently active schedules.
  // Preference order preserved from the original design: a discount-style
  // deal (amount_off/percent_off, or unpriced) is shown ahead of a flat
  // price if both exist, since "$2 off" or "20% off" tends to read as the
  // flashier headline. What's fixed here is that whichever bucket wins
  // now renders its actual value instead of silently dropping it — a
  // "$5 off Select Appetizers" deal used to render as just "Select
  // Appetizers" because the old code only checked whether `price` was
  // null, never whether value/value_type carried a discount.
  const bestFoodDeal = useMemo(() => {
    let discount: { desc: string; value: number | null; valueType: string; venue: string; venueId: string } | null = null
    let priced: { price: number; desc: string; venue: string; venueId: string } | null = null

    for (const v of liveVenues) {
      const activeSchedules = (v.schedules ?? []).filter(s => isScheduleActiveNow(s))
      for (const s of activeSchedules) {
        for (const d of s.deals) {
          if (d.type !== 'food') continue
          const vt = (d as any).value_type
          const isDiscount = vt === 'amount_off' || vt === 'percent_off'
          if (d.price == null && !discount) {
            discount = { desc: d.description, value: (d as any).value ?? null, valueType: vt ?? 'price', venue: v.name, venueId: v.id }
          }
          if (d.price != null && !isDiscount && (!priced || d.price < priced.price)) {
            priced = { price: d.price, desc: d.description, venue: v.name, venueId: v.id }
          }
        }
        // No fallback to deal_text — only show food deals that are
        // explicitly typed as 'food' in the deals array.
        // If no food deals exist, the hero card is hidden entirely.
      }
    }
    return discount ?? priced
  }, [liveVenues])

  const isLive = liveCount > 0

  // CTA label/path based on time state (same logic as before)
  let ctaLabel: string
  let ctaPath: string
  if (isLive) {
    ctaLabel = `See all ${liveCount} open now →`
    ctaPath = '/now'
  } else if (startingSoonCount > 0) {
    ctaLabel = "See what's starting →"
    ctaPath = '/tonight'
  } else {
    ctaLabel = "See tonight's deals →"
    ctaPath = '/tonight'
  }

  const heroStyle: React.CSSProperties = isLive
    ? { background: 'linear-gradient(135deg, #E85D1A 0%, #C94A0A 100%)' }
    : { background: '#3A3630' }

  return (
    <div className="browse-hero-card" style={heroStyle}>
      <div
        className="browse-hero-orb1"
        style={{ background: isLive ? 'rgba(255,255,255,.1)' : 'rgba(34,197,94,.18)' }}
      />
      <div
        className="browse-hero-orb2"
        style={{ background: isLive ? 'rgba(0,0,0,.08)' : 'rgba(59,130,246,.12)' }}
      />

      {isLive && (
        <div className="browse-hero-label" style={{ color: 'rgba(255,255,255,.8)' }}>
          <span className="browse-hero-live-dot" style={{ background: '#fff' }} />
          RIGHT NOW IN {city.toUpperCase()}
        </div>
      )}

      <div className="browse-hero-number">{isLive ? liveCount : venues.length}</div>
      <div className="browse-hero-sub">
        {isLive
          ? `bar${liveCount !== 1 ? 's' : ''} open right now`
          : startingSoonCount > 0
            ? `spot${startingSoonCount !== 1 ? 's' : ''} starting soon`
            : `happy hour spots in ${city}`
        }
      </div>

      {/* Subtext — time context when not live */}
      {!isLive && (
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginBottom: 12, marginTop: -4 }}>
          {nextStart ? `Next happy hour starts in ${nextStart}` : `${totalDeals} deals — updated daily`}
        </div>
      )}

      {/* Deal callouts — only when live and data exists */}
      {isLive && (cheapestDrink || bestFoodDeal) && (
        <div className="browse-hero-callouts">
          {cheapestDrink && (
            <Link
              to={`/venue/${cheapestDrink.venueId}`}
              className="browse-hero-callout"
              style={{ textDecoration: 'none' }}
            >
              <span className="browse-hero-callout-icon">🍺</span>
              <div>
                <div className="browse-hero-callout-label">Cheapest Drink</div>
                <div className="browse-hero-callout-value">
                  {(() => {
                    const label = fmtDealValue({ price: cheapestDrink.price, value: cheapestDrink.value, value_type: cheapestDrink.valueType })
                    return label ? `${label} ${cheapestDrink.desc}` : cheapestDrink.desc
                  })()}
                </div>
                <div className="browse-hero-callout-venue">at {cheapestDrink.venue}</div>
              </div>
            </Link>
          )}
          {bestFoodDeal && (
            <Link
              to={`/venue/${bestFoodDeal.venueId}`}
              className="browse-hero-callout"
              style={{ textDecoration: 'none' }}
            >
              <span className="browse-hero-callout-icon">🍔</span>
              <div>
                <div className="browse-hero-callout-label">Best Food Deal</div>
                <div className="browse-hero-callout-value">
                  {(() => {
                    const isPriced = 'price' in bestFoodDeal
                    const label = isPriced
                      ? fmtDealValue({ price: (bestFoodDeal as any).price })
                      : fmtDealValue({ value: (bestFoodDeal as any).value, value_type: (bestFoodDeal as any).valueType })
                    return label ? `${label} ${bestFoodDeal.desc}` : bestFoodDeal.desc
                  })()}
                </div>
                <div className="browse-hero-callout-venue">at {bestFoodDeal.venue}</div>
              </div>
            </Link>
          )}
        </div>
      )}

      <div className="browse-hero-actions">
        <Link
          to={ctaPath}
          className="browse-hero-btn"
          style={{
            background: isLive ? 'rgba(0,0,0,.22)' : '#22C55E',
            border: isLive ? '1px solid rgba(255,255,255,.25)' : 'none',
            color: '#fff',
          }}
        >
          {ctaLabel}
        </Link>
        <div className="browse-hero-stats">
          <span className="browse-hero-stat" style={{ color: 'rgba(255,255,255,.45)' }}>
            {venues.length} venues
          </span>
          <span className="browse-hero-stat-dot" style={{ color: 'rgba(255,255,255,.25)' }}>·</span>
          <span className="browse-hero-stat" style={{ color: 'rgba(255,255,255,.45)' }}>
            {totalDeals} schedules
          </span>
        </div>
      </div>
    </div>
  )
})

// ─── MAIN PAGE ───────────────────────────────────────────────────────────────

export default function BrowsePage() {
  const { venues, loading, error, userLocation, requestLocation, favorites, city, refetchVenues } = useAppContext()
  const fs = useFilterState()
  const vm = useViewMode()
  const { refreshing } = usePullToRefresh(refetchVenues)

  const [filterOpen, setFilterOpen] = useState(false)
  const [selectedVenueId, setSelectedVenueId] = useState<string | null>(null)
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false)
  const [brandAds, setBrandAds] = useState<BrandAd[]>([])
  const [trendingIds, setTrendingIds] = useState<string[]>([])
  const [upcomingEvents, setUpcomingEvents] = useState<VenueEvent[]>([])
  const [weeklyFeature, setWeeklyFeature] = useState<WeeklyFeature | null>(null)
  const [weeklySponsor, setWeeklySponsor] = useState<BrandAd | null>(null)
  const [visibleCount, setVisibleCount] = useState(INITIAL_PAGE_SIZE)
  const venueCardRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const loadMoreRef = useRef<HTMLDivElement | null>(null)
  const { showCapture, trigger, dismiss: dismissEmail } = useEmailCapture(favorites.count)

  useEffect(() => {
    getActiveBrandAds().then(setBrandAds)

    // Fetch trending venues — most detail_views in last 24h
    // Uses existing venue_impressions table, no new DB changes needed
    const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
    supabase
      .from('venue_impressions')
      .select('venue_id')
      .eq('event_type', 'detail_view')
      .gte('created_at', since)
      .then(({ data }) => {
        if (!data || data.length === 0) return
        const counts: Record<string, number> = {}
        data.forEach((r: any) => {
          counts[r.venue_id] = (counts[r.venue_id] ?? 0) + 1
        })
        const sorted = Object.entries(counts)
          .sort(([, a], [, b]) => b - a)
          .map(([id]) => id)
        setTrendingIds(sorted)
      })

    // Fetch upcoming events
    getUpcomingEvents(30).then(setUpcomingEvents)

    // Happy Hour of the Week (+ its sponsor's brand_ads row, if any)
    getCurrentWeeklyFeature().then(f => {
      setWeeklyFeature(f)
      if (f?.sponsor_ad_id) getBrandAdById(f.sponsor_ad_id).then(setWeeklySponsor)
    })
  }, [])

  useEffect(() => {
    Analytics.seoPageViewed('browse', city)
    // Single app_visits write. The old second call (deviceAnalytics
    // trackPageVisit) hit the UNIQUE(session_id, page) constraint on
    // every load — the data survived, but only because the DB rejected
    // the duplicate. One writer, one source of truth.
    Analytics.pageVisited('browse')
  }, [city])

  useEffect(() => {
    setVisibleCount(INITIAL_PAGE_SIZE)
  }, [fs.filters, showFavoritesOnly])

  const neighborhoods = useMemo(() => getNeighborhoods(venues), [venues])

  const filtered = useMemo(() => {
    let result = sortVenuesByMode(
      filterVenues(venues, fs.filters, userLocation),
      fs.sort,
      userLocation
    )
    if (showFavoritesOnly) {
      result = result.filter(v => favorites.isFavorite(v.id))
    }
    return result
  }, [venues, fs.filters, fs.sort, userLocation, showFavoritesOnly, favorites])

  // Only show Happening Now + Trending Tonight as full rows.
  // Everything else is a category chip that links to a filtered view.
  const bestPicksSections = useMemo(
    () => buildBestPicksSections(venues, userLocation, trendingIds)
          .filter(s => s.id === 'happening_now' || s.id === 'trending_tonight'),
    [venues, userLocation, trendingIds]
  )

  // Compute which category chips to show based on actual venue data.
  // Only show a chip if at least one venue has that deal type or category.
  const activeChips = useMemo(() => {
    const dealTypes = new Set<string>()
    const categories = new Set<string>()
    let hasDogFriendly = false

    for (const v of venues) {
      if (v.dog_friendly) hasDogFriendly = true
      for (const cat of v.categories ?? []) categories.add(cat)
      for (const s of v.schedules ?? []) {
        for (const d of s.deals) dealTypes.add(d.type)
      }
    }

    const ALL_CHIPS = [
      { label: '💰 Cheapest Drinks', dealType: 'beer' },
      { label: '🍸 Cocktail Deals',  dealType: 'cocktail' },
      { label: '🍔 Food Deals',       dealType: 'food' },
      { label: '🍷 Wine Deals',       dealType: 'wine' },
      { label: '🥃 Liquor Deals',     dealType: 'liquor' },
      { label: '🏙️ Rooftop',         category: 'rooftop' },
      { label: '☀️ Patio',            category: 'patio' },
      { label: '🌊 Waterfront',       category: 'waterfront' },
      { label: '🏈 Sports Bar',       category: 'sports_bar' },
      { label: '🍺 Brewery',          category: 'brewery' },
      { label: '🎵 Live Music',       category: 'live_music' },
      { label: '🐾 Dog Friendly',     dogFriendly: true },
    ] as const

    return ALL_CHIPS.filter(chip => {
      if ('dealType' in chip && chip.dealType) return dealTypes.has(chip.dealType)
      if ('category' in chip && chip.category) return categories.has(chip.category)
      if ('dogFriendly' in chip && chip.dogFriendly) return hasDogFriendly
      return false
    })
  }, [venues])

  const visibleVenues = useMemo(
    () => filtered.slice(0, visibleCount),
    [filtered, visibleCount]
  )

  const hasMore = visibleCount < filtered.length
  const isSearching = fs.filters.search.trim().length > 0
  const showBestPicks = !showFavoritesOnly && !isSearching

  // Infinite scroll: grow visibleCount automatically as the sentinel
  // below the list nears the viewport, instead of requiring a tap.
  // rootMargin fires this well before the sentinel is actually visible
  // so the next batch is already rendered by the time the user scrolls
  // there — no visible pause, no empty gap at the bottom.
  useEffect(() => {
    if (!hasMore) return
    const el = loadMoreRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          setVisibleCount(c => c + LOAD_MORE_SIZE)
        }
      },
      { rootMargin: '600px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [hasMore, filtered.length])

  function handleViewDetails(venueId: string) {
    setSelectedVenueId(venueId)
    if (vm.view === 'map') vm.setView('list')
    setTimeout(() => {
      const el = venueCardRefs.current[venueId]
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 100)
  }

  const getDistanceLabel = useCallback((venue: Venue): string | undefined => {
    if (!userLocation || !venue.latitude || !venue.longitude) return undefined
    return fmtDistance(distanceMiles(userLocation.lat, userLocation.lng, venue.latitude, venue.longitude))
  }, [userLocation])

  return (
    <>
      <Helmet>
        <title>Happy Hour Unlocked {city} - Happy Hour Deals Happening Now</title>
        <meta name="description" content={`Happy Hour Unlocked — Find the best happy hour deals in ${city}. Live prices, verified schedules, and deals happening right now.`} />
      </Helmet>

      <div className="browse-page">

        <SplashScreen />

        {loading
          ? <div className="browse-hero-skeleton" aria-hidden="true" />
          : <BrowseHero venues={venues} city={city} />
        }

        {refreshing && (
          <div className="ptr-indicator">
            <span className="ptr-spinner" />
            Refreshing deals...
          </div>
        )}

        {/* Search */}
        <div className="browse-search-bar">
          <div className="browse-search-icon">🔍</div>
          <input
            className="browse-search-input"
            value={fs.filters.search}
            onChange={e => {
              const val = e.target.value
              fs.setSearch(val)
              // Track search with debounce — fires 600ms after user stops typing.
              // Analytics.searchPerformed writes to search_events (deduped per
              // session per query). NOTE: result_count is the count from the
              // render before this keystroke settled — close enough for
              // "what are people searching for", not exact. Known limitation.
              if ((window as any)._searchTimer) clearTimeout((window as any)._searchTimer)
              if (val.trim().length >= 2) {
                ;(window as any)._searchTimer = setTimeout(() => {
                  Analytics.searchPerformed(val.trim(), filtered.length)
                }, 600)
              }
              if (e.target.value.trim()) {
                setTimeout(() => {
                  const el = document.querySelector('.venue-list')
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }, 100)
              }
            }}
            placeholder="Search bars, deals, neighborhoods..."
            aria-label="Search"
          />
          {fs.filters.search && (
            <button className="browse-search-clear" onClick={() => fs.setSearch('')}>✕</button>
          )}
        </div>

        {/* Top bar */}
        <div className="browse-topbar">
          <div className="browse-topbar-left">
            <button
              className={`mobile-filter-btn${filterOpen ? ' active' : ''}${fs.activeCount > 0 ? ' has-filters' : ''}`}
              onClick={() => setFilterOpen(v => !v)}
            >
              <span>⊟</span> Filters
              {fs.activeCount > 0 && <span className="filter-badge">{fs.activeCount}</span>}
            </button>
            <button
              className={`fav-toggle-btn${showFavoritesOnly ? ' active' : ''}`}
              onClick={() => setShowFavoritesOnly(v => !v)}
            >
              ♥ Saved{favorites.count > 0 && ` (${favorites.count})`}
            </button>
          </div>
          <div className="browse-topbar-right">
            <Link to="/crawl" className="crawl-nav-btn">🍺 Build a Crawl</Link>
            <ViewToggle view={vm.view} onSet={v => { vm.setView(v); Analytics.viewModeChanged(v) }} />
          </div>
        </div>

        {/* Open Now indicator */}
        {fs.filters.openNow && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 14px', background: '#DCFCE7', borderRadius: 10,
            margin: '0 0 10px', fontSize: 13, fontWeight: 600, color: '#166534',
          }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22C55E', display: 'inline-block' }} />
            Showing open now only
            <button
              onClick={() => fs.setOpenNow(false)}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#166534', fontWeight: 700 }}
            >
              Show all ✕
            </button>
          </div>
        )}

        {/* Filters */}
        <div className={`filter-panel-wrap${filterOpen ? ' open' : ''}`}>
          <FilterPanel {...fs} venues={venues} neighborhoods={neighborhoods} />
        </div>

        {/* Sort */}
        <SortBar
          sort={fs.sort}
          onSort={fs.setSort}
          userLocation={userLocation}
          onRequestLocation={requestLocation}
          resultCount={filtered.length}
        />

        {loading && <p className="loading-msg">Loading deals...</p>}
        {error && !loading && <p className="error-msg">Couldn't load venues — check your connection and pull down to retry.</p>}

        {/* Events */}
        {!loading && upcomingEvents.length > 0 && (
          <EventsSection events={upcomingEvents} />
        )}

        {/* Best picks — Happening Now + Trending Tonight */}
        {!loading && showBestPicks && (
          <div className="best-picks-area">
            {weeklyFeature && (() => {
              const wfVenue = venues.find(v => v.id === weeklyFeature.venue_id)
              if (!wfVenue) return null
              return (
                <WeeklyFeatureCard
                  feature={weeklyFeature}
                  venue={wfVenue}
                  sponsor={weeklySponsor}
                  isFavorite={favorites.isFavorite(wfVenue.id)}
                  onToggleFavorite={favorites.toggleFavorite}
                />
              )
            })()}

            {bestPicksSections.map(section => (
              <BestPicksRow
                key={section.id}
                section={section}
                favorites={favorites}
                userLocation={userLocation}
              />
            ))}

            {/* Category chips — only show if data exists, grid layout */}
            {activeChips.length > 0 && (
              <div className="category-chips-section">
                <div className="category-chips-label">Browse by category</div>
                <div className="category-chips-grid">
                  {activeChips.map(chip => (
                    <button
                      key={chip.label}
                      className="category-chip"
                      onClick={() => {
                        // Exclusive: clear all chip filters first, then apply the tapped one.
                        // Tapping the already-active chip just clears everything.
                        const dealAlready = 'dealType' in chip && chip.dealType && fs.filters.dealTypes.has(chip.dealType as any)
                        const catAlready  = 'category' in chip && chip.category && fs.filters.categories.has(chip.category as any)
                        const dogAlready  = 'dogFriendly' in chip && chip.dogFriendly && fs.filters.dogFriendly
                        fs.setCategories([])
                        fs.setDealTypes([])
                        fs.setDogFriendly(false)
                        if (!dealAlready && 'dealType' in chip && chip.dealType) fs.toggleDealType(chip.dealType as any)
                        if (!catAlready  && 'category' in chip && chip.category)  fs.toggleCategory(chip.category as any)
                        if (!dogAlready  && 'dogFriendly' in chip && chip.dogFriendly) fs.setDogFriendly(true)
                        setTimeout(() => {
                          const el = document.querySelector('.venue-list')
                          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                        }, 100)
                      }}
                    >
                      {chip.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="all-venues-divider">
              <span>All venues ({filtered.length})</span>
            </div>
          </div>
        )}

        {/* Venue list */}
        {!loading && (
          <div className={`content-area${vm.isSplit ? ' split' : ''}`}>
            {vm.isMap && (
              <div className="map-panel">
                <MapView
                  venues={filtered}
                  selectedVenueId={selectedVenueId}
                  onSelectVenue={setSelectedVenueId}
                  onViewDetails={handleViewDetails}
                  userLocation={userLocation}
                />
              </div>
            )}
            {vm.isList && (
              <div className="venue-list">
                {filtered.length === 0 ? (
                  <div className="hn-empty">
                    <div className="hn-empty-icon">{showFavoritesOnly ? '♥' : '🍺'}</div>
                    <div className="hn-empty-title">
                      {showFavoritesOnly ? 'No saved venues yet' : 'No matches'}
                    </div>
                    <div className="hn-empty-sub">
                      {showFavoritesOnly
                        ? 'Tap the heart on any venue to save it here.'
                        : fs.filters.openNow
                          ? 'No happy hours open right now. Try removing the "open now" filter to see all venues.'
                          : 'Try adjusting your filters or check back later.'
                      }
                    </div>
                    {!showFavoritesOnly && (
                      <button className="hn-empty-btn" onClick={fs.clearAll}>Clear filters</button>
                    )}
                  </div>
                ) : (
                  <>
                    {visibleVenues.flatMap((venue, i) => {
                      const nodes: React.ReactNode[] = [
                        <VenueCard
                          key={venue.id}
                          venue={venue}
                          isFavorite={favorites.isFavorite(venue.id)}
                          onToggleFavorite={favorites.toggleFavorite}
                          showDayContext={true}
                          isSelected={selectedVenueId === venue.id}
                          distanceLabel={getDistanceLabel(venue)}
                          cardRef={el => { venueCardRefs.current[venue.id] = el }}
                          isTrending={trendingIds.slice(0, 5).includes(venue.id)}
                        />
                      ]

                      if (brandAds.length > 0 && (i + 1) % 4 === 0) {
                        const slotIndex = Math.floor(i / 4)
                        nodes.push(
                          <RotatingAd
                            key={`ad-slot-${slotIndex}`}
                            ads={brandAds}
                            slotIndex={slotIndex}
                          />
                        )
                      }

                      return nodes
                    })}

                    {hasMore && (
                      <div
                        ref={loadMoreRef}
                        style={{
                          width: '100%', marginTop: 8, padding: '14px',
                          textAlign: 'center', fontSize: 13,
                          fontWeight: 600, color: '#888',
                        }}
                      >
                        Showing {visibleCount} of {filtered.length}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {showCapture && (
          <EmailCapture trigger={trigger} city={city} onDismiss={dismissEmail} />
        )}

      </div>
    </>
  )
}
